// API Service for IMPRES QB Archive

import authService from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL;

if (!API_BASE_URL) {
  console.error('REACT_APP_API_URL environment variable is not set.');
}

function notifyAuthExpired() {
  window.dispatchEvent(new CustomEvent('auth:session-expired'));
}

async function getAccessToken(forceRefresh = false) {
  try {
    const token = await authService.getIdToken(forceRefresh);
    return token;
  } catch (error) {
    console.warn('Could not get ID token:', error.message);
    return null;
  }
}

async function apiRequest(endpoint, options = {}, isRetry = false) {
  if (!API_BASE_URL) {
    throw new Error('API URL not configured. Check environment variables.');
  }

  const url = `${API_BASE_URL}${endpoint}`;
  const accessToken = await getAccessToken(isRetry);

  // If we couldn't get a token even after a force-refresh, session is expired
  if (!accessToken && isRetry) {
    notifyAuthExpired();
    throw new Error('Your session has expired. Please sign in again.');
  }

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
    },
  };

  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  const response = await fetch(url, mergedOptions);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Network error' }));

    if (response.status === 401) {
      if (!isRetry) {
        console.log('Token expired, refreshing and retrying...');
        return apiRequest(endpoint, options, true);
      }
      notifyAuthExpired();
      throw new Error('Your session has expired. Please sign in again.');
    }

    if (response.status === 403) {
      throw new Error(errorData.message || 'You do not have permission to perform this action.');
    }

    throw new Error(errorData.error || errorData.message || `Request failed: ${response.status}`);
  }

  if (response.status === 204) return null;
  return response.json();
}

// Transactions API
export const transactionsApi = {
  search: (params = {}) => {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return apiRequest(`/transactions${queryString ? `?${queryString}` : ''}`);
  },

  getById: (id) => apiRequest(`/transactions/${id}`),

  getChain: (id) => apiRequest(`/transactions/${id}/chain`),

  getStats: () => apiRequest('/transactions/stats'),

  getEntities: () => apiRequest('/transactions/entities'),

  exportCsv: async (params = {}, _isRetry = false) => {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    const url = `${API_BASE_URL}/transactions/export/csv${queryString ? `?${queryString}` : ''}`;
    const accessToken = await getAccessToken(_isRetry);
    const response = await fetch(url, {
      headers: { ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }) },
    });
    if (response.status === 401) {
      if (!_isRetry) return transactionsApi.exportCsv(params, true);
      notifyAuthExpired();
      throw new Error('Your session has expired. Please sign in again.');
    }
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `qb-archive-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(a);
  },

  getEntityDetail: (entityId, params = {}) => {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return apiRequest(`/transactions/entities/${encodeURIComponent(entityId)}${queryString ? `?${queryString}` : ''}`);
  },
};

// Attachments API
export const attachmentsApi = {
  getDownloadUrl: (id) => `${API_BASE_URL}/attachments/${id}/download`,

  getPreviewUrl: (id) => apiRequest(`/attachments/${id}/preview`),
};

// Import API (admin only)
export const importApi = {
  importTransactions: (transactions) =>
    apiRequest('/import/transactions', {
      method: 'POST',
      body: JSON.stringify({ transactions }),
    }),

  uploadAttachments: async (txnId, files, _isRetry = false) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    const accessToken = await getAccessToken(_isRetry);
    const response = await fetch(`${API_BASE_URL}/import/attachments/${txnId}`, {
      method: 'POST',
      headers: {
        ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
      },
      body: formData,
    });

    if (response.status === 401) {
      if (!_isRetry) return importApi.uploadAttachments(txnId, files, true);
      notifyAuthExpired();
      throw new Error('Your session has expired. Please sign in again.');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(errorData.error || 'Upload failed');
    }

    return response.json();
  },

  importAttachmentsBulk: (attachments) =>
    apiRequest('/import/attachments-bulk', {
      method: 'POST',
      body: JSON.stringify({ attachments }),
    }),

  getJobs: () => apiRequest('/import/jobs'),

  getJob: (id) => apiRequest(`/import/jobs/${id}`),
};

// Users API
export const usersApi = {
  getAll: () => apiRequest('/users'),

  updateRoles: (id, roles) =>
    apiRequest(`/users/${id}/roles`, {
      method: 'PUT',
      body: JSON.stringify({ roles }),
    }),

  syncAllFromEntra: () =>
    apiRequest('/users/sync-all', { method: 'POST' }),
};
